import React, { useState, useEffect } from 'react';
import {
  Card, Upload, Button, message, Image, Space, Typography, Alert, Modal, Switch, Row, Col,
  ColorPicker, Slider, Segmented, InputNumber, Tooltip
} from 'antd';
import {
  UploadOutlined, DeleteOutlined, PictureOutlined,
  InfoCircleOutlined, WarningOutlined, BulbOutlined, AimOutlined
} from '@ant-design/icons';
import { eventoService, fixUrl } from '../../../services/api';
import { heroOverlayRgba } from '../../../utils/hero';
import { altoHeaderDe } from '../../../utils/micrositioTema';

const { Title, Text, Paragraph } = Typography;

// Encuadre del fondo. Los valores por defecto (50/50/cover) reproducen el
// "center/cover" que estaba hardcodeado, asi que un evento que nunca toco
// estos controles se sigue viendo igual.
const POS_X_PRESETS = [
  { label: 'Izquierda', value: 0 },
  { label: 'Centro', value: 50 },
  { label: 'Derecha', value: 100 },
];
const POS_Y_PRESETS = [
  { label: 'Arriba', value: 0 },
  { label: 'Medio', value: 50 },
  { label: 'Abajo', value: 100 },
];
const ALIGN_V_PRESETS = [
  { label: 'Arriba', value: 'flex-start' },
  { label: 'Medio', value: 'center' },
  { label: 'Abajo', value: 'flex-end' },
];

const val = (config, clave, porDefecto) =>
  (config[clave] != null ? config[clave] : porDefecto);

// Toggles de visibilidad del hero por tipo de página. Default: visible.
// hero_off_* = true significa OCULTAR (sin banner, header sólido arriba).
const HERO_PAGINAS = [
  { key: 'hero_off_catalogo', label: 'Catálogo (página principal)' },
  { key: 'hero_off_ficha',    label: 'Ficha individual del animal' },
  { key: 'hero_off_internas', label: 'Páginas internas (condiciones, contacto)' },
];

/**
 * @param {function} [subirImagen]    (file) => Promise<url|null>. Inyectable.
 * @param {function} [eliminarImagen] () => Promise<void>. Inyectable.
 *
 * Las dos son OPCIONALES y por defecto hacen lo de siempre: guardar en
 * `evento.imgsec_evento`. Existen porque este tab se reusa en el panel del
 * catalogo de genetica, donde la imagen del hero NO es una columna sino una
 * clave del JSON de configuracion (`hero_img`) — no hay ningun PUT de columna
 * al que colgarse.
 *
 * Se inyectan en vez de meter un `if (esCatalogo)` adentro: la validacion de
 * tipo y tamaño, el preview, el encuadre y los modales de ayuda son IGUALES en
 * los dos casos, y lo unico que cambia es donde aterriza el archivo.
 */
/**
 * @param {string[]} [ocultar]  Claves de config que esta pantalla NO debe
 *   ofrecer, igual que en `EstructuraTab`. El catalogo de genetica reusa este
 *   tab pero su `CatalogoGenetica.set_config()` WHITELISTEA contra
 *   `DEFAULT_CATALOGO_CONFIG`: una clave que no esta ahi se descarta en
 *   silencio al guardar. Sin esto el productor mueve el slider del margen,
 *   aprieta Guardar, no pasa nada y al releer vuelve a "automatico".
 *   Default `[]`: el micrositio de remate sigue mostrando todo igual.
 */
const FondoHeroTab = ({
  eventoId, imagenActual, onImagenCargada, config = {}, onChange,
  subirImagen, eliminarImagen, ocultar = [],
}) => {
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(imagenActual);

  // useState(prop) lee la prop SOLO en el primer montaje. El tab se re-usa al
  // cambiar de evento (el padre no le pasa key), asi que sin esto el productor
  // veia la imagen del evento anterior mientras `eventoId` ya era el nuevo: si
  // tocaba Eliminar, borraba la imagen correcta viendo la equivocada.
  useEffect(() => {
    setPreviewImage(imagenActual);
  }, [imagenActual]);
  
  // Estados para modals
  const [modalInfo, setModalInfo] = useState(false);
  const [modalEspecificaciones, setModalEspecificaciones] = useState(false);
  const [modalConsejo, setModalConsejo] = useState(false);

  // El comportamiento de SIEMPRE, extraido tal cual para que sea el default.
  // El segundo intento con `getById` no es defensivo de mas: hay respuestas del
  // PUT que no traen la URL nueva, y sin releerla el productor sube una imagen
  // y ve la vieja.
  const subirAEvento = async (file) => {
    const formData = new FormData();
    formData.append('imgsec_evento', file);
    const { data } = await eventoService.update(eventoId, formData);
    const url = data.evento?.imgsec_evento ?? data.imgsec_evento ?? null;
    if (url) return url;
    const { data: reloadData } = await eventoService.getById(eventoId);
    return reloadData.imgsec_evento || null;
  };

  const handleUpload = async (file) => {
    // Validar tipo de archivo
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('Solo se permiten archivos de imagen');
      return false;
    }

    // Validar tamaño (máximo 5MB)
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('La imagen debe ser menor a 5MB');
      return false;
    }

    setUploading(true);

    try {
      const nuevaImagenUrl = subirImagen
        ? await subirImagen(file)
        : await subirAEvento(file);

      if (nuevaImagenUrl) {
        setPreviewImage(nuevaImagenUrl);
        message.success('Imagen de fondo cargada exitosamente');
        if (onImagenCargada) onImagenCargada(nuevaImagenUrl);
      } else {
        message.warning('Imagen subida. Recarga la página para verla.');
      }
    } catch (error) {
      console.error('Error al subir imagen:', error);
      // error.message de axios es "Request failed with status code 413": el
      // motivo real (tamanio, formato) viene en la respuesta del backend.
      message.error(error.response?.data?.error || 'Error al subir la imagen de fondo');
    } finally {
      setUploading(false);
    }
    return false; // Prevenir upload automático de antd
  };

  const handleEliminar = async () => {
    setUploading(true);
    
    try {
      if (eliminarImagen) {
        await eliminarImagen();
      } else {
        const formData = new FormData();
        formData.append('imgsec_evento', '');
        await eventoService.update(eventoId, formData);
      }

      setPreviewImage(null);
      message.success('Imagen de fondo eliminada');
      
      if (onImagenCargada) {
        onImagenCargada(null);
      }
    } catch (error) {
      console.error('Error al eliminar imagen:', error);
      message.error(error.response?.data?.error || 'Error al eliminar la imagen');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ padding: '16px 0' }}>
      <Title level={4}>
        <PictureOutlined /> Imagen de Fondo Hero Zone
      </Title>

      {/* PRIMERO EL ARCHIVO, despues como se ve. Este Card estaba al FONDO del
          tab, detras de la visibilidad, la altura y el overlay: el productor
          entraba a subir una foto y lo primero que encontraba eran las opciones
          para configurar una imagen que todavia no existia. El orden de una
          pantalla tiene que seguir el orden en que se hacen las cosas. */}
      <Card
        title={previewImage ? 'Cambiar imagen' : 'Subir imagen'}
        style={{ marginBottom: 24 }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Upload
            accept="image/*"
            showUploadList={false}
            beforeUpload={handleUpload}
            disabled={uploading}
          >
            <Button 
              icon={<UploadOutlined />} 
              size="large"
              loading={uploading}
              type={previewImage ? "default" : "primary"}
              block
            >
              {uploading ? 'Subiendo...' : 'Cargar Imagen'}
            </Button>
          </Upload>
        </Space>
      </Card>

      {/* Visibilidad del Hero por tipo de página */}
      <Card title="Mostrar el Hero Zone" style={{ marginBottom: 24 }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Activá o desactivá la franja del hero en cada parte del micrositio. Al desactivarla, el contenido arranca debajo del header (sin banner).
        </Text>
        <Row gutter={[16, 16]}>
          {HERO_PAGINAS.map(p => (
            <Col xs={24} sm={8} key={p.key}>
              <Space>
                <Switch
                  checked={!config[p.key]}
                  onChange={(checked) => onChange && onChange(p.key, !checked)}
                  checkedChildren="Sí"
                  unCheckedChildren="No"
                />
                <Text>{p.label}</Text>
              </Space>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Altura del hero zone */}
      {(() => {
        // Alto actual real según el modo (transparente usa 300, sólido 210),
        // para que el slider arranque mostrando lo que se ve hoy.
        const tipoCab = config.cabecera || 'cabecera-1';
        const isTwoRow = tipoCab === 'cabecera-2' || tipoCab === 'cabecera-3';
        const heroTransp = !isTwoRow && (config.color_header_initial || 'transparent') === 'transparent';
        const altoDefault = heroTransp ? 300 : 210;
        const altoActual = config.hero_altura || altoDefault;
        // Margen superior: el automático es el mismo que calculan las páginas
        // del micrositio (`heroMargenSuperior` en utils/hero.js): 0 con hero
        // transparente, el alto teórico de la cabecera si es sólida. Se muestra
        // para que el productor sepa de qué número parte antes de fijarlo.
        const margenAuto = heroTransp ? 0 : altoHeaderDe(config);
        const margenManual = config.hero_margen_superior != null && config.hero_margen_superior !== '';
        const margenActual = margenManual ? Number(config.hero_margen_superior) : margenAuto;
        // El catalogo de genetica no lee esta clave (ver `ocultar` arriba): sin
        // esconder la columna, el slider seria un control que no hace nada.
        const margenOculto = ocultar.includes('hero_margen_superior');
        return (
          <Card title={margenOculto ? 'Altura del Hero Zone' : 'Altura y margen del Hero Zone'} style={{ marginBottom: 24 }}>
            <Row gutter={[24, 16]}>
              <Col xs={24} md={margenOculto ? 24 : 12}>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>Altura</Text>
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  Más grande = más protagonismo de la imagen; más chico = más compacto.
                </Text>
                <Slider
                  min={120}
                  max={600}
                  step={10}
                  value={altoActual}
                  onChange={(v) => onChange && onChange('hero_altura', v)}
                />
                <Text strong>{altoActual} px</Text>
                {config.hero_altura && (
                  <Button type="link" size="small" style={{ padding: '0 0 0 8px' }} onClick={() => onChange && onChange('hero_altura', null)}>
                    Restaurar
                  </Button>
                )}
              </Col>
              {!margenOculto && (
              <Col xs={24} md={12}>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>Margen superior</Text>
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  Espacio entre el borde de arriba y el hero. En automático toma el alto de la cabecera elegida; fijalo a mano si tu cabecera quedó más alta o más baja que eso.
                </Text>
                <Slider
                  min={0}
                  max={240}
                  step={2}
                  value={margenActual}
                  onChange={(v) => onChange && onChange('hero_margen_superior', v)}
                />
                <Text strong>{margenActual} px</Text>
                {margenManual ? (
                  <Button type="link" size="small" style={{ padding: '0 0 0 8px' }} onClick={() => onChange && onChange('hero_margen_superior', null)}>
                    Restaurar automático
                  </Button>
                ) : (
                  <Text type="secondary" style={{ marginLeft: 8 }}>(automático)</Text>
                )}
              </Col>
              )}
            </Row>
          </Card>
        );
      })()}

      {/* Encuadre de la imagen de fondo */}
      {(() => {
        const posX = Number(val(config, 'hero_bg_pos_x', 50));
        const posY = Number(val(config, 'hero_bg_pos_y', 50));
        const modo = val(config, 'hero_bg_size', 'cover');
        const pct = Number(val(config, 'hero_bg_size_pct', 100));
        const sizeCss = modo === 'porcentaje' ? `${pct}%` : modo;
        // Alto real del hero segun el modo de cabecera, igual que en la card
        // de Altura: transparente arranca en 300, solido en 210.
        const tipoCab = config.cabecera || 'cabecera-1';
        const dosFilas = tipoCab === 'cabecera-2' || tipoCab === 'cabecera-3';
        const transparente = !dosFilas && (config.color_header_initial || 'transparent') === 'transparent';
        const altoPorDefecto = transparente ? 300 : 210;
        const sinImagen = !previewImage;
        const alineacion = val(config, 'hero_text_align_v', 'center');
        const padTop = Number(val(config, 'hero_text_padding_top', 0));
        const padBottom = Number(val(config, 'hero_text_padding_bottom', 0));
        const hayCambios = posX !== 50 || posY !== 50 || modo !== 'cover'
          || alineacion !== 'center' || padTop !== 0 || padBottom !== 0;

        return (
          <Card
            title={<><AimOutlined /> Encuadre de la imagen de fondo</>}
            style={{ marginBottom: 24 }}
            extra={hayCambios && (
              <Button
                type="link"
                size="small"
                onClick={() => {
                  if (!onChange) return;
                  onChange('hero_bg_pos_x', 50);
                  onChange('hero_bg_pos_y', 50);
                  onChange('hero_bg_size', 'cover');
                  onChange('hero_bg_size_pct', 100);
                  onChange('hero_text_align_v', 'center');
                  onChange('hero_text_padding_top', 0);
                  onChange('hero_text_padding_bottom', 0);
                }}
              >
                Restaurar todo
              </Button>
            )}
          >
            <Text type="secondary" style={{ display: 'block', marginBottom: 20 }}>
              Decidí qué parte de la foto se ve. Sirve cuando el motivo importante
              (un animal, el logo) queda cortado o descentrado.
            </Text>

            {sinImagen && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 20 }}
                message="Todavía no cargaste una imagen de fondo"
                description="Los controles funcionan igual y quedan guardados, pero no vas a ver el efecto hasta que subas una imagen."
              />
            )}

            <Row gutter={[24, 20]}>
              {/* ── Posición horizontal ── */}
              <Col xs={24} md={12}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  Alineación horizontal
                </Text>
                <Segmented
                  block
                  options={POS_X_PRESETS}
                  value={POS_X_PRESETS.some((o) => o.value === posX) ? posX : null}
                  onChange={(v) => onChange && onChange('hero_bg_pos_x', v)}
                  style={{ marginBottom: 10 }}
                />
                <Row gutter={12} align="middle">
                  <Col flex="auto">
                    <Slider
                      min={0}
                      max={100}
                      value={posX}
                      tooltip={{ formatter: (v) => `${v}%` }}
                      onChange={(v) => onChange && onChange('hero_bg_pos_x', v)}
                    />
                  </Col>
                  <Col flex="86px">
                    <InputNumber
                      min={0}
                      max={100}
                      value={posX}
                      addonAfter="%"
                      size="small"
                      style={{ width: '100%' }}
                      onChange={(v) => v != null && onChange && onChange('hero_bg_pos_x', v)}
                    />
                  </Col>
                </Row>
              </Col>

              {/* ── Posición vertical ── */}
              <Col xs={24} md={12}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  Alineación vertical
                </Text>
                <Segmented
                  block
                  options={POS_Y_PRESETS}
                  value={POS_Y_PRESETS.some((o) => o.value === posY) ? posY : null}
                  onChange={(v) => onChange && onChange('hero_bg_pos_y', v)}
                  style={{ marginBottom: 10 }}
                />
                <Row gutter={12} align="middle">
                  <Col flex="auto">
                    <Slider
                      min={0}
                      max={100}
                      value={posY}
                      tooltip={{ formatter: (v) => `${v}%` }}
                      onChange={(v) => onChange && onChange('hero_bg_pos_y', v)}
                    />
                  </Col>
                  <Col flex="86px">
                    <InputNumber
                      min={0}
                      max={100}
                      value={posY}
                      addonAfter="%"
                      size="small"
                      style={{ width: '100%' }}
                      onChange={(v) => v != null && onChange && onChange('hero_bg_pos_y', v)}
                    />
                  </Col>
                </Row>
              </Col>

              {/* ── Tamaño ── */}
              <Col xs={24}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  Tamaño de la imagen
                </Text>
                <Row gutter={[12, 12]} align="middle">
                  <Col xs={24} sm={10}>
                    <Segmented
                      block
                      options={[
                        { label: <Tooltip title="Llena todo el hero, recortando lo que sobra">Cubrir</Tooltip>, value: 'cover' },
                        { label: <Tooltip title="Entra completa, puede dejar franjas vacías">Contener</Tooltip>, value: 'contain' },
                        { label: 'Porcentaje', value: 'porcentaje' },
                      ]}
                      value={modo}
                      onChange={(v) => onChange && onChange('hero_bg_size', v)}
                    />
                  </Col>
                  {modo === 'porcentaje' && (
                    <>
                      <Col xs={16} sm={10}>
                        <Slider
                          min={10}
                          max={300}
                          step={5}
                          value={pct}
                          tooltip={{ formatter: (v) => `${v}%` }}
                          onChange={(v) => onChange && onChange('hero_bg_size_pct', v)}
                        />
                      </Col>
                      <Col xs={8} sm={4}>
                        <InputNumber
                          min={10}
                          max={300}
                          value={pct}
                          addonAfter="%"
                          size="small"
                          style={{ width: '100%' }}
                          onChange={(v) => v != null && onChange && onChange('hero_bg_size_pct', v)}
                        />
                      </Col>
                    </>
                  )}
                </Row>
              </Col>

              {/* ── Posición del texto ── */}
              <Col xs={24}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  Posición del título dentro del hero
                </Text>
                <Row gutter={[12, 12]} align="middle">
                  <Col xs={24} sm={10}>
                    <Segmented
                      block
                      options={ALIGN_V_PRESETS}
                      value={alineacion}
                      onChange={(v) => onChange && onChange('hero_text_align_v', v)}
                    />
                  </Col>
                  <Col xs={12} sm={7}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Separar de arriba</Text>
                    <InputNumber
                      min={0}
                      max={200}
                      value={padTop}
                      addonAfter="px"
                      size="small"
                      style={{ width: '100%' }}
                      onChange={(v) => onChange && onChange('hero_text_padding_top', v || 0)}
                    />
                  </Col>
                  <Col xs={12} sm={7}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Separar de abajo</Text>
                    <InputNumber
                      min={0}
                      max={200}
                      value={padBottom}
                      addonAfter="px"
                      size="small"
                      style={{ width: '100%' }}
                      onChange={(v) => onChange && onChange('hero_text_padding_bottom', v || 0)}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>

            {/* ── Vista previa en vivo ── */}
            <Text strong style={{ display: 'block', margin: '24px 0 8px' }}>Vista previa</Text>
            <div
              style={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 8,
                border: '1px solid #f0f0f0',
                // Mismo criterio que la card de Altura: con header
                // transparente el hero arranca en 300, si no en 210. Con 210
                // fijo la preview mentia sobre el alto real.
                minHeight: config.hero_altura || altoPorDefecto,
                display: 'flex',
                justifyContent: 'center',
                alignItems: alineacion,
                background: previewImage
                  ? `url(${fixUrl(previewImage)}) ${posX}% ${posY}%/${sizeCss} no-repeat`
                  : `linear-gradient(135deg, ${config.color_primary || '#1a5f2a'} 0%, ${config.color_secondary || '#8B4513'} 100%)`,
                backgroundColor: '#e8e8e8',
              }}
            >
              <div style={{ position: 'absolute', inset: 0, background: heroOverlayRgba(config) }} />
              <span
                style={{
                  position: 'relative',
                  color: config.color_hero_text || '#fff',
                  fontWeight: 700,
                  fontSize: config.hero_text_size || 32,
                  textAlign: 'center',
                  padding: `${padTop}px 24px ${padBottom}px`,
                  textTransform: config.text_transform_hero || 'none',
                  lineHeight: 1.15,
                }}
              >
                Título del remate
              </span>
            </div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
              CSS aplicado: <code>background-position: {posX}% {posY}%</code> · <code>background-size: {sizeCss}</code>
            </Text>
          </Card>
        );
      })()}

      {/* Capa de color sobre la imagen del hero (overlay) */}
      <Card title="Capa de color sobre la imagen (overlay)" style={{ marginBottom: 24 }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Tinta la imagen del hero con un color y transparencia para que el título se lea mejor. Subí la transparencia para oscurecer/aclarar la foto.
        </Text>
        <Row gutter={[24, 16]} align="middle">
          <Col xs={24} sm={8}>
            <Text style={{ display: 'block', marginBottom: 8 }}>Color</Text>
            <ColorPicker
              value={config.hero_overlay_color || '#000000'}
              onChange={(c) => onChange && onChange('hero_overlay_color', `#${c.toHex()}`)}
              showText
            />
          </Col>
          <Col xs={24} sm={16}>
            <Text style={{ display: 'block', marginBottom: 8 }}>
              Transparencia: {Math.round((config.hero_overlay_opacity != null ? config.hero_overlay_opacity : 0.25) * 100)}%
            </Text>
            <Slider
              min={0}
              max={1}
              step={0.05}
              value={config.hero_overlay_opacity != null ? config.hero_overlay_opacity : 0.25}
              onChange={(v) => onChange && onChange('hero_overlay_opacity', v)}
            />
          </Col>
        </Row>
        <div style={{
          marginTop: 12, height: 60, borderRadius: 8,
          background: `linear-gradient(rgba(0,0,0,0), rgba(0,0,0,0)), #888`,
          position: 'relative', overflow: 'hidden',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            position: 'absolute', inset: 0,
            background: heroOverlayRgba(config),
          }} />
          <Text style={{ position: 'relative', color: '#fff', fontWeight: 700 }}>Vista previa del overlay</Text>
        </div>
      </Card>

      {/* Alerts informativos con click para abrir modals */}
      <Space direction="vertical" style={{ width: '100%', marginBottom: 24 }}>
        <Alert
          message={
            <span style={{ cursor: 'pointer' }} onClick={() => setModalInfo(true)}>
              <InfoCircleOutlined /> Sobre la Imagen de Fondo (1920x600)
            </span>
          }
          type="info"
          showIcon={false}
          style={{ cursor: 'pointer' }}
          onClick={() => setModalInfo(true)}
        />
        
        <Alert
          message={
            <span style={{ cursor: 'pointer' }} onClick={() => setModalEspecificaciones(true)}>
              <WarningOutlined /> Especificaciones
            </span>
          }
          type="warning"
          showIcon={false}
          style={{ cursor: 'pointer' }}
          onClick={() => setModalEspecificaciones(true)}
        />
        
        <Alert
          message={
            <span style={{ cursor: 'pointer' }} onClick={() => setModalConsejo(true)}>
              <BulbOutlined /> Consejo
            </span>
          }
          type="success"
          showIcon={false}
          style={{ cursor: 'pointer' }}
          onClick={() => setModalConsejo(true)}
        />
      </Space>

      {/* Preview de la imagen actual */}
      {previewImage && (
        <Card 
          title="Imagen Actual"
          style={{ marginBottom: 24 }}
          extra={
            <Button 
              danger 
              icon={<DeleteOutlined />}
              onClick={handleEliminar}
              loading={uploading}
              size="small"
            >
              Eliminar
            </Button>
          }
        >
          <Image
            src={fixUrl(previewImage)}
            alt="Fondo Hero Zone"
            style={{ 
              width: '100%', 
              maxHeight: 300, 
              objectFit: 'cover',
              borderRadius: 8 
            }}
            preview={{
              mask: 'Ver imagen completa'
            }}
          />
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">
              Dimensiones recomendadas: 1920x600px
            </Text>
          </div>
        </Card>
      )}

      {/* Modal: Sobre la Imagen de Fondo */}
      <Modal
        title={
          <span>
            <InfoCircleOutlined style={{ color: '#1890ff', marginRight: 8 }} />
            Sobre la Imagen de Fondo
          </span>
        }
        open={modalInfo}
        onCancel={() => setModalInfo(false)}
        footer={[
          <Button key="cerrar" type="primary" onClick={() => setModalInfo(false)}>
            Entendido
          </Button>
        ]}
        width={600}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Paragraph>
            Esta imagen se utilizará como <strong>fondo en la zona principal (Hero Zone)</strong> de tu micrositio público. 
            Es lo primero que verán tus visitantes al entrar al sitio.
          </Paragraph>
          
          <div style={{ background: '#f0f2f5', padding: 16, borderRadius: 8 }}>
            <Title level={5}>📐 Dimensiones Recomendadas</Title>
            <Paragraph style={{ marginBottom: 0 }}>
              <strong>1920 x 600 píxeles</strong> (Relación 16:5)
              <br />
              Esta medida garantiza que la imagen se vea bien en pantallas de escritorio sin distorsión.
            </Paragraph>
          </div>

          <div style={{ background: '#e6f7ff', padding: 16, borderRadius: 8 }}>
            <Title level={5}>💡 Recomendaciones</Title>
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              <li>Usa imágenes de <strong>alta calidad</strong> que representen tu cabaña</li>
              <li>Evita imágenes con <strong>mucho texto</strong>, ya que se superpondrá contenido</li>
              <li>Prefiere imágenes <strong>horizontales panorámicas</strong></li>
              <li>El contenido importante debe estar en el <strong>centro de la imagen</strong></li>
            </ul>
          </div>

          <Alert
            message="📝 Nota Importante"
            description="Si al crear el evento ya subiste una imagen secundaria, esta se utilizará automáticamente como fondo del Hero Zone. Puedes cambiarla aquí en cualquier momento."
            type="info"
            showIcon
          />
        </Space>
      </Modal>

      {/* Modal: Especificaciones */}
      <Modal
        title={
          <span>
            <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
            Especificaciones Técnicas
          </span>
        }
        open={modalEspecificaciones}
        onCancel={() => setModalEspecificaciones(false)}
        footer={[
          <Button key="cerrar" type="primary" onClick={() => setModalEspecificaciones(false)}>
            Entendido
          </Button>
        ]}
        width={500}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Title level={5}>📄 Formatos Permitidos</Title>
            <Space wrap>
              <Button type="dashed" size="small">JPG</Button>
              <Button type="dashed" size="small">JPEG</Button>
              <Button type="dashed" size="small">PNG</Button>
              <Button type="dashed" size="small">WebP</Button>
              <Button type="dashed" size="small">GIF</Button>
            </Space>
          </div>

          <div>
            <Title level={5}>📏 Tamaño del Archivo</Title>
            <Paragraph>
              <strong>Máximo: 5 MB</strong>
              <br />
              <Text type="secondary">
                Archivos más grandes pueden tardar en cargar y afectar la experiencia del usuario.
              </Text>
            </Paragraph>
          </div>

          <div>
            <Title level={5}>📐 Dimensiones</Title>
            <Paragraph>
              <strong>Recomendadas: 1920 x 600 píxeles</strong>
              <br />
              <strong>Relación de aspecto: 16:5</strong>
              <br />
              <Text type="secondary">
                Imágenes más pequeñas pueden verse pixeladas. Imágenes más grandes se redimensionarán automáticamente.
              </Text>
            </Paragraph>
          </div>

          <Alert
            message="💡 Tip"
            description="Puedes usar herramientas como Photoshop, GIMP, Canva o cualquier editor de imágenes online para redimensionar tus imágenes a las medidas exactas."
            type="info"
            showIcon
          />
        </Space>
      </Modal>

      {/* Modal: Consejo */}
      <Modal
        title={
          <span>
            <BulbOutlined style={{ color: '#52c41a', marginRight: 8 }} />
            Consejos para una Mejor Imagen
          </span>
        }
        open={modalConsejo}
        onCancel={() => setModalConsejo(false)}
        footer={[
          <Button key="cerrar" type="primary" onClick={() => setModalConsejo(false)}>
            ¡Gracias por el consejo!
          </Button>
        ]}
        width={600}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div style={{ background: '#f6ffed', padding: 16, borderRadius: 8, border: '1px solid #b7eb8f' }}>
            <Title level={5}>🎨 Qué Imagen Usar</Title>
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              <li>Imágenes que <strong>representen tu cabaña</strong> o establecimiento</li>
              <li>Fotos de tus <strong>mejores animales</strong> en primer plano</li>
              <li>Paisajes de tu <strong>campo o instalaciones</strong></li>
              <li>Imágenes de <strong>eventos anteriores</strong> exitosos</li>
            </ul>
          </div>

          <div style={{ background: '#fff7e6', padding: 16, borderRadius: 8, border: '1px solid #ffd591' }}>
            <Title level={5}>⚠️ Qué Evitar</Title>
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              <li>Imágenes con <strong>mucho texto</strong> - Se superpondrá el contenido del sitio</li>
              <li>Fotos <strong>muy oscuras</strong> - El texto puede ser difícil de leer</li>
              <li>Imágenes <strong>verticales</strong> - No se adaptarán bien al formato panorámico</li>
              <li>Fotos de <strong>baja calidad</strong> o pixeladas</li>
            </ul>
          </div>

          <div style={{ background: '#e6f7ff', padding: 16, borderRadius: 8, border: '1px solid #91d5ff' }}>
            <Title level={5}>🎯 Composición Ideal</Title>
            <Paragraph style={{ marginBottom: 0 }}>
              Coloca el <strong>contenido importante en el centro</strong> de la imagen. 
              Los bordes izquierdo y derecho pueden quedar ocultos en pantallas pequeñas, 
              así que evita poner información crucial ahí.
            </Paragraph>
          </div>

          <Alert
            message="🌟 Pro Tip"
            description="Antes de subir la imagen, ábrela en tu computadora y coloca una ventana del navegador encima para simular cómo se verá el texto sobre ella. Así podrás evaluar si el contraste es adecuado."
            type="success"
            showIcon
          />
        </Space>
      </Modal>
    </div>
  );
};

export default FondoHeroTab;
